// ═══════════════════════════════════════════════════════════════════════════
// Parser de direcciones pegadas — lote 2.5, observación 1
//
// El cliente manda su dirección en un bloque de texto:
//
//     Jorge Bucio
//     Cerrada de Arcos 1122
//     Col. Prados de la Sierra
//     San Pedro Garza García 66230
//
// Estrategia: EL CÓDIGO POSTAL ES EL ANCLA. Se localiza primero, se deduce el
// estado con certeza (ver codigos-postales.js), y el resto se resuelve por
// marcas explícitas ("Col.", "C.P.") antes que por posición. Adivinar por
// posición es lo que produce basura.
//
// Nunca rellena en silencio: devuelve `confianza` por campo para que la
// interfaz muestre vista previa y el usuario confirme.
// ═══════════════════════════════════════════════════════════════════════════

import { estadoPorCP, esCPValido, mismoEstado } from "./codigos-postales.js";

const RE_CP        = /\b(\d{5})\b/;
const RE_CP_MARCA  = /\bC\.?\s?P\.?\s*:?\s*(\d{5})\b/i;
const RE_COLONIA   = /^\s*(?:col(?:onia)?\.?|fracc(?:ionamiento)?\.?|barrio|unidad hab(?:itacional)?\.?|residencial)\s+(.+)$/i;
const RE_TELEFONO  = /(?:tel(?:[ée]fono)?\.?\s*:?\s*)?(\+?\d[\d\s()-]{8,}\d)/i;
const RE_CORREO    = /[\w.+-]+@[\w-]+\.[\w.]+/;
const RE_CALLE     = /\b(?:calle|av(?:enida)?\.?|blvd\.?|boulevard|carr(?:etera)?\.?|priv(?:ada)?\.?|cerrada|and(?:ador)?\.?|circuito|camino|prolongaci[óo]n|paseo|callej[óo]n|km)\b/i;
const RE_NUMERO    = /(?:^|\s)(?:#|no\.?|n[úu]m\.?)?\s*\d+[a-zA-Z]?\b/;
const RE_INTERIOR  = /\b(?:int(?:erior)?\.?|depto\.?|departamento|casa|local|piso|edificio)\b/i;

// Palabras que delatan una línea de CIUDAD/ESTADO y no de calle
const RE_ESTADO_TXT = /\b(baja california|sonora|sinaloa|chihuahua|coahuila|nuevo le[óo]n|tamaulipas|durango|zacatecas|jalisco|nayarit|colima|michoac[áa]n|guanajuato|quer[ée]taro|hidalgo|puebla|tlaxcala|veracruz|oaxaca|guerrero|morelos|m[ée]xico|cdmx|ciudad de m[ée]xico|distrito federal|tabasco|campeche|yucat[áa]n|quintana roo|chiapas|san luis potos[íi]|aguascalientes)\b/i;

/**
 * Convierte un bloque de texto libre en campos de domicilio.
 *
 * @returns {{
 *   name, street, colonia, cp, city, state, phone, email,
 *   confianza: Record<string,'marca'|'cp'|'posicion'|'texto'>,
 *   avisos: string[],
 *   sinUsar: string[]        // líneas que el parser no supo clasificar
 * }}
 */
export function parsearDireccion(texto, { esperaNombre = true } = {}) {
  const r = {
    name: "", street: "", colonia: "", cp: "", city: "", state: "",
    phone: "", email: "",
    confianza: {}, avisos: [], sinUsar: [],
  };
  if (!texto || !texto.trim()) return r;

  // Una dirección puede venir en una sola línea con comas
  let lineas = texto.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lineas.length === 1 && (lineas[0].match(/,/g) || []).length >= 2) {
    lineas = lineas[0].split(",").map(l => l.trim()).filter(Boolean);
  }

  const usada = new Array(lineas.length).fill(false);
  const marcar = (i, campo, valor, conf) => {
    if (!valor || r[campo]) return;
    r[campo] = valor.trim().replace(/\s{2,}/g, " ").replace(/[,;]+$/, "");
    r.confianza[campo] = conf;
    usada[i] = true;
  };

  // ── 1. Contacto (se retira para no confundir al resto) ──
  lineas.forEach((l, i) => {
    const correo = l.match(RE_CORREO);
    if (correo) { marcar(i, "email", correo[0], "marca"); }
    if (!r.phone && /tel|cel|whats/i.test(l)) {
      const t = l.match(RE_TELEFONO);
      if (t) marcar(i, "phone", t[1], "marca");
    }
  });

  // ── 2. CP: el ancla ──
  //
  // ⚠ Un número de casa puede tener 5 dígitos y parecer CP: el domicilio real
  //   "Anillo Vial Fray Junípero Serra 11376" hacía que el parser tomara 11376
  //   como código postal (prefijo 11 = CDMX) y descarrilara todo lo demás.
  //   Por eso el orden de búsqueda: marca explícita → última línea con el
  //   número AL FINAL → cualquiera. Las direcciones ponen el CP al final.
  let iCP = -1;
  const fijarCP = (i, cp, conf) => { r.cp = cp; r.confianza.cp = conf; iCP = i; };

  for (let i = 0; i < lineas.length && !r.cp; i++) {         // (a) "C.P. 12345"
    const m = lineas[i].match(RE_CP_MARCA);
    if (m && esCPValido(m[1])) fijarCP(i, m[1], "marca");
  }
  for (let i = lineas.length - 1; i >= 0 && !r.cp; i--) {    // (b) al final, de atrás
    const m = lineas[i].match(/\b(\d{5})\s*$/);
    if (m && esCPValido(m[1])) fijarCP(i, m[1], "cp");
  }
  for (let i = lineas.length - 1; i >= 0 && !r.cp; i--) {    // (c) donde sea
    const m = lineas[i].match(RE_CP);
    if (m && esCPValido(m[1])) fijarCP(i, m[1], "posicion");
  }

  if (r.cp) {
    r.state = estadoPorCP(r.cp);
    r.confianza.state = "cp";
  } else {
    r.avisos.push("No se encontró un código postal válido: el estado no pudo deducirse.");
  }

  // ── 3. Estado escrito en el texto: verificar, no sobrescribir ──
  lineas.forEach((l) => {
    const m = l.match(RE_ESTADO_TXT);
    if (!m) return;
    if (r.state && !mismoEstado(m[0], r.state)) {
      r.avisos.push(`El texto dice "${m[0]}" pero el CP ${r.cp} corresponde a ${r.state}. Se usó el del CP; verifica.`);
    } else if (!r.state) {
      r.state = m[0]; r.confianza.state = "texto";
    }
  });

  // ── 4. Colonia por marca explícita ──
  lineas.forEach((l, i) => {
    const m = l.match(RE_COLONIA);
    if (m) marcar(i, "colonia", m[1], "marca");
  });

  // ── 5. Ciudad: en la línea del CP, quitando CP y estado ──
  //
  // Dos trampas reales, ambas detectadas con datos del operador:
  //   · "Delegación Miguel Hidalgo" contiene "Hidalgo", que es un ESTADO.
  //     Quitar el estado a ciegas la truncaba a "Delegación Miguel".
  //     → solo se quita si aparece al FINAL o tras una coma.
  //   · Muchas ciudades se llaman igual que su estado (Chihuahua, Querétaro,
  //     Guanajuato). Quitarlo dejaba la ciudad VACÍA.
  //     → si al quitarlo no queda nada, se conserva el texto completo.
  if (iCP >= 0) {
    const sinCP = lineas[iCP]
      .replace(RE_CP_MARCA, " ").replace(RE_CP, " ")
      .replace(/\s{2,}/g, " ").trim();

    // El estado solo cuenta si está al final (con o sin coma antes)
    const RE_ESTADO_FINAL = new RegExp(`(?:^|,)\\s*${RE_ESTADO_TXT.source}\\s*[,.]?\\s*$`, "i");
    let ciudad = sinCP.replace(RE_ESTADO_FINAL, " ");
    ciudad = ciudad.replace(/[,;]+\s*$/, "").replace(/\s{2,}/g, " ").trim();

    // Si quitar el estado la dejó vacía, la ciudad SE LLAMA como el estado
    if (!ciudad) ciudad = sinCP.replace(/[,;.]+\s*$/, "").trim();

    if (ciudad.length >= 3 && !RE_COLONIA.test(lineas[iCP])) {
      marcar(iCP, "city", ciudad, "cp");
    }
  }

  // ── 6. Calle: línea con número que no sea colonia ni ciudad ──
  lineas.forEach((l, i) => {
    if (usada[i] || r.street) return;
    if (RE_COLONIA.test(l)) return;
    const tieneNumero = RE_NUMERO.test(l);
    // RE_INTERIOR solo vale acompañado de dígitos: "M&J CASA GOURMET S. DE
    // R.L. DE C.V." contiene "CASA" y se clasificaba como calle.
    const pareceCalle = RE_CALLE.test(l) || (RE_INTERIOR.test(l) && /\d/.test(l));
    if (tieneNumero || pareceCalle) marcar(i, "street", l, pareceCalle ? "marca" : "posicion");
  });

  // ── 7. Nombre: primera línea sin usar, si se espera ──
  if (esperaNombre && !r.name) {
    const i = lineas.findIndex((l, k) => !usada[k] && !RE_ESTADO_TXT.test(l) && l.length >= 3);
    if (i >= 0 && i <= 1) marcar(i, "name", lineas[i], "posicion");
  }

  // ── 7b. Calle sin número ni palabra clave ("Ocurre", "Mercado Municipal") ──
  // Van entre el nombre y la colonia; se aceptan por posición, con confianza baja.
  if (!r.street) {
    const iColonia = lineas.findIndex(l => RE_COLONIA.test(l));
    const i = lineas.findIndex((l, k) =>
      !usada[k] && l.length >= 3 &&
      (iColonia < 0 || k < iColonia) && (iCP < 0 || k < iCP));
    if (i >= 0) marcar(i, "street", lineas[i], "posicion");
  }

  // ── 8. Rellenos por posición para lo que falte ──
  if (!r.city) {
    const i = lineas.findIndex((l, k) => !usada[k] && !RE_NUMERO.test(l) && l.length >= 3);
    if (i >= 0) marcar(i, "city", lineas[i], "posicion");
  }
  if (!r.colonia) {
    const i = lineas.findIndex((l, k) => !usada[k] && l.length >= 3);
    if (i >= 0) marcar(i, "colonia", lineas[i], "posicion");
  }

  r.sinUsar = lineas.filter((_, i) => !usada[i]);
  if (r.sinUsar.length) {
    r.avisos.push(`No se clasificaron ${r.sinUsar.length} línea(s): revísalas antes de guardar.`);
  }
  if (!r.street) r.avisos.push("No se identificó la calle.");

  return r;
}

/** Formatea un cliente como bloque de texto (para pruebas de ida y vuelta). */
export function comoBloque(c) {
  return [
    c.name,
    c.street,
    c.colonia ? `Col. ${c.colonia}` : "",
    [c.city, c.cp].filter(Boolean).join(" "),
  ].filter(Boolean).join("\n");
}
